import React, { useState } from 'react';
import { ArrowRight, Target, Shield, Briefcase, Zap, Network, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ContactDialog from '../components/ContactDialog';
import { companyInfo, services, customers } from '../mock';

const Home = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const serviceIcons = {
    1: Zap,
    2: Target,
    3: Briefcase,
    4: Network,
    5: Shield,
    6: Lock
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
<img
  src="https://kancalawas.com/uploads/utama.jpg"
  alt="Data Center"
  className="w-full h-full object-cover"
/>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-sky-900/75"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto space-y-6">
            <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight animate-fade-in">
              {companyInfo.name}
            </h1>
            <p className="text-xl md:text-2xl text-sky-300 font-semibold">
              {companyInfo.slogan}
            </p>
            <div className="w-24 h-1 bg-sky-500 mx-auto rounded-full"></div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Tentang Kami</h2>
              <div className="w-24 h-1 bg-sky-500 mx-auto rounded-full"></div>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed text-justify">
              {companyInfo.description}
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRlYW13b3JrJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3Nzk0MzE2Mzd8MA&ixlib=rb-4.1.0&q=85"
                alt="Team Collaboration"
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="order-1 md:order-2 space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Visi & Misi</h2>
                <div className="w-24 h-1 bg-sky-500 rounded-full mb-6"></div>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed text-justify">
                {companyInfo.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Policy Section */}
      <section className="py-20 bg-gradient-to-br from-sky-50 via-white to-sky-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Kebijakan Mutu, Keselamatan dan Kesehatan Kerja
                </h2>
                <div className="w-24 h-1 bg-sky-500 rounded-full mb-6"></div>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed text-justify">
                {companyInfo.qualityPolicyTitle}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed text-justify">
                {companyInfo.qualityPolicyDesc}
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1690378820474-b468b8ee64d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW13b3JrJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3Nzk0MzE2Mzd8MA&ixlib=rb-4.1.0&q=85"
                alt="Safety Standards"
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Layanan</h2>
            <div className="w-24 h-1 bg-sky-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service) => {
              const IconComponent = serviceIcons[service.id];
              return (
                <Card
                  key={service.id}
                  className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-gray-100 hover:border-sky-300"
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-sky-500 to-sky-600 rounded-xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 leading-tight">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Contact Button */}
          <div className="text-center mt-16">
            <Button
              onClick={() => setIsContactOpen(true)}
              size="lg"
              className="bg-sky-600 hover:bg-sky-700 text-white font-semibold px-10 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              Hubungi Kami
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Customers Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Customer</h2>
            <div className="w-24 h-1 bg-sky-500 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
            {customers.map((customer) => (
              <Card
                key={customer.id}
                className="group hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <CardContent className="p-6 flex flex-col items-center justify-center space-y-3">
                  <div className="w-full h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center overflow-hidden transform group-hover:scale-105 transition-transform duration-300">
                    <img
                      src={customer.logo}
                      alt={customer.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm font-medium text-gray-700 text-center">{customer.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Dialog */}
      <ContactDialog open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
};

export default Home;